import {
    appendMethodToScopeScript,
    isValidJsIdentifier,
    listScopeScriptMembers,
} from './controlDesignerScopeScript.js';
import { isScopeScriptEmpty } from '../../../Shared/scopeLibraryCore.js';

export { isScopeScriptEmpty } from '../../../Shared/scopeLibraryCore.js';

function isCompiledExpression(value) {
    return value
        && typeof value === 'object'
        && typeof value.func === 'function'
        && 'code' in value;
}

function normalizeInterpolatedSource(original) {
    if (!original || typeof original !== 'string')
        return '';

    return original
        .replace(/\{\*/g, '{{')
        .replace(/\*\}/g, '}}')
        .replace(/\{#/g, '{{')
        .replace(/#\}/g, '}}')
        .replace(/\{%/g, '{{')
        .replace(/%\}/g, '}}')
        .replace(/\\\{/g, '&Opnc;')
        .replace(/\\\}/g, '&Clsc;');
}

export function emitExpressionSegment(segment, deps) {
    const raw = String(segment || '')
        .replace(/&Opnc;/g, '{')
        .replace(/&Clsc;/g, '}');

    const withFilters = deps.extractFilters(raw);
    return deps.parseExpressionWithFilters(withFilters);
}

export function emitInterpolatedString(original, deps) {
    const source = normalizeInterpolatedSource(original);
    const segments = [];
    let last = 0;
    const matcher = /\{\{([^}]+)\}\}/g;
    let match;

    while ((match = matcher.exec(source)) !== null) {
        if (match.index > last) {
            const staticText = source.slice(last, match.index);
            if (staticText)
                segments.push(JSON.stringify(staticText));
        }

        segments.push(emitExpressionSegment(match[1], deps));
        last = match.index + match[0].length;
    }

    if (last < source.length) {
        const staticText = source.slice(last);
        if (staticText)
            segments.push(JSON.stringify(staticText));
    }

    if (segments.length === 0)
        return JSON.stringify(original || '');

    if (segments.length === 1)
        return segments[0];

    return segments.join(' + ');
}

export function emitObjFuncExpression(objFunc, deps) {
    const source = objFunc?.code ?? '';
    if (/\{\{/.test(source))
        return emitInterpolatedString(source, deps);

    const trimmed = String(source).trim();
    if (trimmed)
        return trimmed;

    return 'null';
}

function isActionArray(value) {
    return Array.isArray(value)
        && value.some((item) => item && typeof item === 'object' && 'ActionType' in item);
}

function shouldEmitLiteralInitialValue(initialValue) {
    if (typeof initialValue === 'number' || typeof initialValue === 'boolean')
        return true;

    return typeof initialValue === 'string'
        && !initialValue.startsWith('{#')
        && !/\{\{/.test(initialValue);
}

export function compileLegacyVariableInitialValue(initialValue, deps) {
    if (shouldEmitLiteralInitialValue(initialValue))
        return initialValue;

    if (typeof initialValue === 'string' && !initialValue.startsWith('{#'))
        return deps.compile(initialValue);

    if (Array.isArray(initialValue))
        return initialValue.map((item) => compileLegacyVariableInitialValue(item, deps));

    if (initialValue && typeof initialValue === 'object') {
        const result = {};
        for (const key of Object.keys(initialValue))
            result[key] = compileLegacyVariableInitialValue(initialValue[key], deps);
        return result;
    }

    return deps.compileObject(initialValue);
}

export function emitInitializerExpression(compiled, deps) {
    if (compiled === null || compiled === undefined)
        return 'null';

    const valueType = typeof compiled;

    if (valueType === 'boolean' || valueType === 'number')
        return String(compiled);

    if (valueType === 'string')
        return JSON.stringify(compiled);

    if (isCompiledExpression(compiled))
        return emitObjFuncExpression(compiled, deps);

    if (Array.isArray(compiled)) {
        const items = compiled.map((item) => emitInitializerExpression(item, deps));
        return `[${items.join(', ')}]`;
    }

    if (valueType === 'object') {
        const entries = [];
        for (const key of Object.keys(compiled)) {
            if (isActionArray(compiled[key]))
                entries.push(`${JSON.stringify(key)}: ${JSON.stringify(compiled[key])}`);
            else
                entries.push(`${JSON.stringify(key)}: ${emitInitializerExpression(compiled[key], deps)}`);
        }
        return `{ ${entries.join(', ')} }`;
    }

    return JSON.stringify(compiled);
}

function buildVariableMemberBlock(variable, deps) {
    const name = String(variable?.Name || '').trim();
    if (!name || !isValidJsIdentifier(name))
        return null;

    const initializer = emitInitializerExpression(
        compileLegacyVariableInitialValue(variable?.InitialValue, deps),
        deps
    );

    return `\t${name}: ${initializer}`;
}

function buildFunctionMemberBlock(func) {
    const name = String(func?.Name || '').trim();
    const body = String(func?.Body || '').trim();
    if (!name || !isValidJsIdentifier(name) || !body)
        return null;

    return `\t${name}: ${body}`;
}

function mergeScopePublic(existing, legacyPublicNames) {
    const merged = new Set([
        ...(Array.isArray(existing) ? existing : []),
        ...legacyPublicNames,
    ]);

    return [...merged].sort();
}

/**
 * @param {unknown} variables
 * @param {unknown} functions
 * @returns {boolean}
 */
export function hasLegacyScopeItems(variables, functions) {
    return (Array.isArray(variables) && variables.length > 0)
        || (Array.isArray(functions) && functions.length > 0);
}

/**
 * @param {{ ScopeScript?: string | null } | null | undefined} model
 * @returns {boolean}
 */
export function isScopeScriptUnset(model) {
    if (!model)
        return true;

    if (!('ScopeScript' in model))
        return true;

    return isScopeScriptEmpty(model.ScopeScript);
}

/**
 * @param {{ ScopeScript?: string | null, Variables?: unknown, Functions?: unknown } | null | undefined} model
 * @returns {boolean}
 */
export function shouldDeferScopeScriptInitialization(model) {
    if (!model)
        return false;

    return isScopeScriptUnset(model)
        && hasLegacyScopeItems(model.Variables, model.Functions);
}

/**
 * @param {{
 *   variables?: { Name?: string, Public?: boolean, InitialValue?: unknown }[],
 *   functions?: { Name?: string, Public?: boolean, Body?: string }[],
 *   scopeScript?: string,
 *   scopePublic?: string[],
 * }} input
 * @param {{
 *   compile: (text: string) => unknown,
 *   compileObject: (model: unknown) => unknown,
 *   extractFilters: (segment: string) => string,
 *   parseExpressionWithFilters: (expr: string) => string,
 * }} deps
 */
export function migrateLegacyScopeToScopeScript(input = {}, deps) {
    const variables = Array.isArray(input.variables) ? input.variables : [];
    const functions = Array.isArray(input.functions) ? input.functions : [];
    let scopeScript = input.scopeScript || '({\n})';
    const existingMembers = new Set(
        listScopeScriptMembers(scopeScript).map((member) => member.name)
    );

    const migrated = [];
    const skipped = [];
    const legacyPublicNames = [];

    const tryAppend = (name, block) => {
        if (!name || !block) {
            if (name)
                skipped.push({ name, reason: 'Invalid member name or body.' });
            return;
        }

        if (existingMembers.has(name)) {
            skipped.push({ name, reason: 'Already exists in ScopeScript.' });
            return;
        }

        scopeScript = appendMethodToScopeScript(scopeScript, block);
        existingMembers.add(name);
        migrated.push(name);
    };

    for (const variable of variables) {
        const name = String(variable?.Name || '').trim();
        if (variable?.Public && name)
            legacyPublicNames.push(name);

        tryAppend(name, buildVariableMemberBlock(variable, deps));
    }

    for (const func of functions) {
        const name = String(func?.Name || '').trim();
        if (func?.Public && name)
            legacyPublicNames.push(name);

        tryAppend(name, buildFunctionMemberBlock(func));
    }

    return {
        scopeScript,
        scopePublic: mergeScopePublic(input.scopePublic, legacyPublicNames),
        migrated,
        skipped,
    };
}
